<template>
    <div class="select-floorplan-input">
        <div class="icon-container">
            <icon name="ci-layer" color="var(--secondary)" />
        </div>
        <treeselect
            v-model="selectedOption"
            :clearable="false"
            :multiple="false"
            :disable-branch-nodes="true"
            :options="formattedOptions"
            noChildrenText="N/A"
            ref="select-input"
            @open="adjustMenuWidth"
        >
            <label
                slot="option-label"
                slot-scope="{ node, shouldShowCount, count, labelClassName, countClassName }"
                :class="{ labelClassName, 'option-label-is-not-in-filter': !findInProject(node.id) }"
            >
                {{ node.label }}
            </label>
        </treeselect>
    </div>
</template>

<script>
import Treeselect from '@riophae/vue-treeselect'
import '@riophae/vue-treeselect/dist/vue-treeselect.css'

const MIN_MENU_WIDTH = 158

export default {
    components: {
        Treeselect,
    },
    props: {
        project: Object,
        currentSector: Object,
        currentFloorplan: Object,
        allOptionEnabled: {
            type: Boolean,
            default: true,
        },
        showSectors: {
            type: Boolean,
            default: false,
        },
        datesToFilter: {
            type: Array,
            default: () => {
                return []
            },
        },
        value: {
            default: null,
        },
    },
    watch: {
        value() {
            this.selectedOption = this.value
        },
        selectedOption: {
            handler() {
                this.$emit('input', this.selectedOption)
                this.$emit(
                    'updateSelectedLocationName',
                    this.formattedOptions.find((option) => option.id == this.selectedOption)
                )
            },
            deep: true,
        },
        currentFloorplan() {
            this.selectedOption = 'floorplan-' + this.currentFloorplan.id
            this.$emit(
                'updateSelectedLocationName',
                this.formattedOptions.find((option) => option.id == this.selectedOption)
            )
        },
        currentSector() {
            this.setupFromSector()
        },
        formattedOptions() {
            this.$emit(
                'updateSelectedLocationName',
                this.formattedOptions.find((option) => option.id == this.selectedOption)
            )
        },
    },
    data() {
        return {
            selectedOption: null,
            formattedOptions: [],
            options: [],
            menuWidth: MIN_MENU_WIDTH,
        }
    },
    methods: {
        adjustMenuWidth() {
            this.$nextTick(() => {
                let vue_treeselect = this.$refs['select-input'].$el
                if (vue_treeselect && this.menuWidth > MIN_MENU_WIDTH) {
                    vue_treeselect.lastChild.firstChild.style.setProperty('width', this.menuWidth + 'px')
                }
            }, 500)
        },
        setupFromProject() {
            let allLengths = []
            if (this.project.map) {
                if (this.showSectors) {
                    this.formattedOptions = [
                        {
                            id: 'floorplan-' + this.project.map.id,
                            label: this.$t('keyword49'),
                        },
                        ...this.project.map.sectors.map((sec) => {
                            allLengths.push(sec.name.length)
                            return {
                                id: 'sector-' + sec.id,
                                label: sec.name,
                                children: [
                                    ...sec.floorplans.map((fp) => {
                                        allLengths.push(fp.name.length)
                                        return {
                                            id: 'floorplan-' + fp.id,
                                            label: fp.name,
                                        }
                                    }),
                                ],
                            }
                        }),
                    ]
                    if (this.project.floorplans.length) {
                        this.formattedOptions.push({
                            id: 'floorplan-unlinked',
                            label: this.$t('Components_Viewer_SectorSelectionViewer_1'),
                            children: this.project.floorplans.map((fp) => {
                                return {
                                    id: 'floorplan-' + fp.id,
                                    label: fp.name,
                                }
                            }),
                        })
                    }
                } else {
                    this.formattedOptions = [
                        {
                            id: 'all',
                            label: this.$t('keyword157'),
                        },

                        {
                            id: 'floorplan-' + this.project.map.id,
                            label: this.$t('keyword49'),
                        },

                        ...this.project.map.sectors.reduce((allFloorplans, sec) => {
                            return [
                                ...allFloorplans,
                                ...sec.floorplans.map((fp) => {
                                    allLengths.push(fp.name.length)
                                    return {
                                        id: 'floorplan-' + fp.id,
                                        label: fp.name,
                                    }
                                }),
                            ]
                        }, []),

                        ...this.project.floorplans.map((fp) => {
                            allLengths.push(fp.name.length)
                            return {
                                id: 'floorplan-' + fp.id,
                                label: fp.name,
                            }
                        }),
                    ]
                }
            } else {
                this.formattedOptions = this.project.floorplans.map((fp) => {
                    allLengths.push(fp.name.length)
                    return {
                        id: 'floorplan-' + fp.id,
                        label: fp.name,
                    }
                })
                if (this.allOptionEnabled) {
                    this.formattedOptions.unshift({ id: 'all', label: this.$t('keyword157') })
                }
            }
            this.menuWidth = Math.max(...allLengths) * 12
        },
        setupFromSector() {
            let allLengths = []
            if (this.currentSector && this.currentSector.floorplans) {
                this.formattedOptions = this.currentSector.floorplans.map((fp) => {
                    allLengths.push(fp.name.length)
                    return {
                        id: 'floorplan-' + fp.id,
                        label: fp.name,
                    }
                })
                if (this.allOptionEnabled) {
                    this.formattedOptions.unshift({ id: 'all', label: this.$t('keyword157') })
                }
            }
            this.menuWidth = Math.max(...allLengths) * 12
        },
        // FILTERS
        findInProject(opt) {
            if (opt == 'all' || opt == 'floorplan-unlinked') {
                return true
            }
            if (opt.includes('sector')) {
                let sectorId = opt.replace('sector-', '')
                let flag = false
                this.project.map.sectors.forEach((sct) => {
                    if (sct.id == sectorId) {
                        flag = this.checkSectorDate(sct)
                    }
                })
                return flag
            } else if (opt.includes('floorplan')) {
                let floorplanId = opt.replace('floorplan-', '')
                if (this.project.map) {
                    if (this.project.map.id == floorplanId) {
                        return this.checkFloorplanDate(this.project.map)
                    } else {
                        let flag = false
                        this.project.map.sectors.forEach((sct) => {
                            sct.floorplans.forEach((fp) => {
                                if (fp.id == floorplanId) {
                                    flag = this.checkFloorplanDate(fp)
                                }
                            })
                        })
                        this.project.floorplans.forEach((fp) => {
                            if (fp.id == floorplanId) {
                                flag = this.checkFloorplanDate(fp)
                            }
                        })
                        return flag
                    }
                }
                // is NOT Map
                else {
                    let flag = false
                    this.project.floorplans.forEach((fp) => {
                        if (fp.id == floorplanId) {
                            flag = this.checkFloorplanDate(fp)
                        }
                    })
                    return flag
                }
            }
        },
        checkSectorDate(sector) {
            if (this.datesToFilter && this.datesToFilter.length == 0) return true
            let flag = false
            if (sector.floorplans) {
                sector.floorplans.forEach((fp) => {
                    if (fp.viewpoints) {
                        fp.viewpoints.forEach((vp) => {
                            vp.images.forEach((img) => {
                                let imgDate = img.date.split(' ')[0]
                                this.datesToFilter.forEach((date) => {
                                    if (date == imgDate) {
                                        flag = true
                                    }
                                })
                            })
                        })
                    }
                })
            }
            return flag
        },
        checkFloorplanDate(floorplan) {
            if (this.datesToFilter && this.datesToFilter.length == 0) return true
            let flag = false
            if (floorplan.viewpoints) {
                floorplan.viewpoints.forEach((vp) => {
                    vp.images.forEach((img) => {
                        let imgDate = img.date.split(' ')[0]
                        this.datesToFilter.forEach((date) => {
                            if (date == imgDate) {
                                flag = true
                            }
                        })
                    })
                })
            }
            return flag
        },
    },
    mounted() {
        this.selectedOption = 'floorplan-' + this.currentFloorplan.id
        if (this.project) {
            this.$nextTick(() => {
                this.setupFromProject()
            }, 500)
        } else if (this.currentSector) {
            this.setupFromSector()
        }
        this.$emit(
            'updateSelectedLocationName',
            this.formattedOptions.find((option) => option.id == this.selectedOption)
        )
    },
}
</script>

<style lang="scss">
label {
    cursor: pointer;
}
.option-label-is-not-in-filter {
    color: var(--secondary-3) !important;
}

// .vue-treeselect__option {
//     width: 348px !important;
// }

.select-floorplan-input {
    .vue-treeselect {
        border: none !important;
        outline: none;
        .vue-treeselect__control {
            height: 32px;
            border: none !important;
            outline: none;
            box-shadow: none !important;
            input {
                box-shadow: none;
                color: var(--light);
                font-size: 16px;
                font-weight: bold;
                outline: none;
            }
        }
    }
}
</style>

<style lang="scss" scoped>
.select-floorplan-input {
    display: flex;
    flex-flow: row;
    align-items: center;
    justify-content: flex-start;
    height: 32px;
    background-color: var(--primary-3);
    padding: 8px;
    border-radius: 4px;

    .icon-container {
        width: 32px;
        margin-right: 8px;
    }
}
</style>
